import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { confirm } from '@/core/asyncActions/confirm/confirm';
import { getUseOperationZones } from '@/redux/launchDarkly/launchDarkly.slice';
import { getUserToken } from '@/redux/user/user.slice';
import {
    useAssignOperationZoneMutation,
    useGetRestaurantOperationZonesQuery,
    useRemoveOperationZoneMutation,
    useSetPrimaryOperationZoneMutation,
} from '@/services/innovorder/operationZone/operationZone.endpoints';
import { useGetVatConfigurationQuery } from '@/services/innovorder/vatConfiguration/vatConfiguration.endpoints';
import { useGetBrandOperationZoneStatusQuery } from '@/services/innovorder/brand/brand.endpoints';
import type { RestaurantOperationZoneDetailDto } from '@/services/innovorder/operationZone/operationZone.type';
import { COUNTRY_FLAG_MAP, isZoneFullyConfigured } from './OperationZones.type';
import type { Territory } from './OperationZones.type';
import { isTerritoryCompatibleWithBrand } from './OperationZones.utils';

export const useOperationZonesVM = () => {
    const intl = useIntl();
    const { brandId, restaurantId } = useParams<{ brandId: string; restaurantId: string }>();
    const token = useSelector(getUserToken);
    const operationZonesEnabled = useSelector(getUseOperationZones);

    const { data: vatConfigData, isLoading: isLoadingZones } = useGetVatConfigurationQuery({ token });

    const { data: operationZonesData, isLoading: isLoadingConfigured } = useGetRestaurantOperationZonesQuery(
        { restaurantId: Number(restaurantId), token },
        { skip: !restaurantId },
    );

    const { data: brandOpZoneStatus } = useGetBrandOperationZoneStatusQuery(
        { brandId: Number(brandId), token: token || '' },
        { skip: !token || !brandId || !operationZonesEnabled },
    );

    const brandCurrency = brandOpZoneStatus?.brandCurrency ?? null;
    const hasOpZoneRestaurant = brandOpZoneStatus?.hasOpZoneRestaurant ?? false;

    const [assignOperationZone, { isLoading: isAssigning }] = useAssignOperationZoneMutation();
    const [removeOperationZone, { isLoading: isRemoving }] = useRemoveOperationZoneMutation();
    const [setPrimaryOperationZone, { isLoading: isSettingPrimary }] = useSetPrimaryOperationZoneMutation();

    const isMutating = isAssigning || isRemoving || isSettingPrimary;

    const territories: Territory[] = useMemo(() => {
        if (!vatConfigData) return [];

        return vatConfigData.operationZones.map((zone) => ({
            id: zone.code,
            name: zone.name,
            code: zone.code,
            flag: COUNTRY_FLAG_MAP[zone.countryCode] ?? '\u{1F3F3}\u{FE0F}',
            solution: zone.fiscalityType.length > 1 ? 'operationZones.multiProvider' : zone.fiscalityType[0],
            availableProviders: zone.fiscalityType,
            countryCode: zone.countryCode,
            currencies: zone.currencies,
        }));
    }, [vatConfigData]);

    const selectedTerritoryIds = useMemo(() => {
        if (!operationZonesData) return [];
        return operationZonesData.zones.map((z) => z.operationZone.code);
    }, [operationZonesData]);

    const primaryTerritoryId = useMemo(() => {
        if (!operationZonesData) return null;
        return operationZonesData.zones.find((z) => z.isPrimary)?.operationZone.code ?? null;
    }, [operationZonesData]);

    const configuredZonesMap = useMemo(() => {
        const map = new Map<string, RestaurantOperationZoneDetailDto>();
        if (!operationZonesData) return map;
        operationZonesData.zones.forEach((zone) => {
            map.set(zone.operationZone.code, zone);
        });
        return map;
    }, [operationZonesData]);

    const isTerritoryDisabledByCurrency = useCallback(
        (territory: Territory): boolean => {
            if (selectedTerritoryIds.includes(territory.id)) return false;
            return !isTerritoryCompatibleWithBrand(territory, brandCurrency, hasOpZoneRestaurant);
        },
        [hasOpZoneRestaurant, brandCurrency, selectedTerritoryIds],
    );

    const toggleTerritory = useCallback(
        async (id: string) => {
            if (!restaurantId || isMutating) return;
            const numericRestaurantId = Number(restaurantId);

            if (selectedTerritoryIds.includes(id)) {
                if (selectedTerritoryIds.length <= 1) return;

                const hasConfirmed = await confirm(
                    intl.formatMessage({ id: 'operationZones.deactivate.message' }),
                    intl.formatMessage({ id: 'operationZones.deactivate.title' }),
                );
                if (!hasConfirmed) return;
                removeOperationZone({ restaurantId: numericRestaurantId, operationZoneCode: id, token });
            } else {
                const territory = territories.find((t) => t.id === id);
                if (territory && isTerritoryDisabledByCurrency(territory)) return;
                assignOperationZone({
                    restaurantId: numericRestaurantId,
                    operationZoneCode: id,
                    isPrimary: selectedTerritoryIds.length === 0,
                    token,
                });
            }
        },
        [
            restaurantId,
            token,
            selectedTerritoryIds,
            isMutating,
            assignOperationZone,
            removeOperationZone,
            intl,
            territories,
            isTerritoryDisabledByCurrency,
        ],
    );

    const hasSelectedTerritories = selectedTerritoryIds.length > 0;

    const unconfiguredCount = useMemo(() => {
        if (!operationZonesData) return 0;
        return operationZonesData.zones.filter((z) => !isZoneFullyConfigured(z)).length;
    }, [operationZonesData]);

    const hasUnconfiguredZones = unconfiguredCount > 0;

    const primaryTerritory = useMemo(
        () => territories.find((t) => t.id === primaryTerritoryId) ?? null,
        [territories, primaryTerritoryId],
    );

    const secondaryTerritories = useMemo(
        () => territories.filter((t) => selectedTerritoryIds.includes(t.id) && t.id !== primaryTerritoryId),
        [territories, selectedTerritoryIds, primaryTerritoryId],
    );

    const setPrimaryTerritory = useCallback(
        (id: string) => {
            if (!restaurantId || isMutating) return;
            if (!selectedTerritoryIds.includes(id)) return;

            const zoneDetail = configuredZonesMap.get(id);
            if (!zoneDetail || !isZoneFullyConfigured(zoneDetail)) return;

            setPrimaryOperationZone({ restaurantId: Number(restaurantId), operationZoneCode: id, token });
        },
        [restaurantId, token, selectedTerritoryIds, isMutating, setPrimaryOperationZone, configuredZonesMap],
    );

    return {
        territories,
        selectedTerritoryIds,
        selectedCount: selectedTerritoryIds.length,
        toggleTerritory,
        primaryTerritoryId,
        primaryTerritory,
        secondaryTerritories,
        setPrimaryTerritory,
        hasSelectedTerritories,
        hasUnconfiguredZones,
        unconfiguredCount,
        configuredZonesMap,
        isMutating,
        isLoading: isLoadingZones || isLoadingConfigured,
        isTerritoryDisabledByCurrency,
    };
};
